import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  DoCheck,
  EventEmitter,
  inject,
  Input,
  KeyValueDiffer,
  KeyValueDiffers,
  OnChanges,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgForm } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MercerDataService } from '@coin/shared/master-data/data-access';
import { MercerJobCodeSearchablePropertyEnum, MercerSurveyDecision } from '@coin/shared/master-data/util';
import { CustomerSurveyService } from '@coin/shared/customer-survey/data-access';
import { CustomerSurveyComponent } from '@coin/shared/customer-survey/feature';
import {
  CustomerSurvey,
  CustomerSurveyQuestion,
  CustomerSurveyResult,
  MERCER_DEFAULT_SURVEY,
  MERCER_PROJECT_MANAGEMENT_SURVEY
} from '@coin/shared/customer-survey/util';
import { ConfirmationDialogComponent } from '@coin/shared/core/ui-storybook';
import { MercerSurveyType } from '@coin/shared/core/util-enums';
import { ObservableCachingHelper, TinyHelpers } from '@coin/shared/core/util-helpers';
import { MercerData } from '@coin/shared/core/util-models';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { ToastrService } from 'ngx-toastr';
import { delayWhen, forkJoin, Observable, of } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { MercerDialogType } from '../../enums/mercer-dialog-type.enum';
import { MercerSupportDetailsComponentTab } from '../../enums/mercer-support-details-tabs.enum';
import { MercerSupportHelper } from '../../helpers/mercer-support.helper';
import { MercerEvaluationMemberDetails } from '../../models/mercer-support-evaluation-member.model';
import { MercerSupportDetailsService } from '../../services/mercer-support-details.service';
import { MercerService } from '../../services/mercer.service';
import { PermissionsService } from '@coin/shared/auth/feature';

@Component({
  selector: 'coin-c-position-survey-results',
  templateUrl: './position-survey-results.component.html',
  styleUrls: ['./position-survey-results.component.scss'],
  standalone: false
})
export class PositionSurveyResultsComponent implements OnChanges, DoCheck, OnInit {
  mercerSupportDetailsService = inject(MercerSupportDetailsService);
  private differs = inject(KeyValueDiffers);
  private customerSurveyService = inject(CustomerSurveyService);
  private mercerDataService = inject(MercerDataService);
  private store = inject(Store);
  private dialog = inject(MatDialog);
  private toast = inject(ToastrService);
  private translate = inject(TranslateService);
  private mercerService = inject(MercerService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private permissionsService = inject(PermissionsService);

  @Input({ required: true }) memberDetails?: MercerEvaluationMemberDetails;
  @Input() derivedPositionClass: number;
  @Input({ required: true }) currentMercerData: MercerData;
  @Input() dialogType: MercerDialogType;
  @Input() isFinalizationAllowed: boolean;
  @Input() isMassDialog = false;
  @Input() disablePositionClass = false;
  @Input() isLocked: boolean;
  @Input() isEditable: boolean;
  @Input() pcInputErrorMessage: string;

  @Output() public finalizePositionClass = new EventEmitter<void>();
  @Output() acceptProposedPositionClass = new EventEmitter<void>();
  @Output() changePositionClass = new EventEmitter<number>();
  // emitter for update/create Regular surveys
  @Output() setCareerInformation = new EventEmitter<CustomerSurveyResult>();
  // emitter for update/create PM surveys
  @Output() setJobCodeAndPositionClass = new EventEmitter<CustomerSurveyResult>();
  // emitter for update/create regular surveys for headcount
  @Output() setCareerInformationHeadcount = new EventEmitter<CustomerSurvey>();

  @ViewChild(CustomerSurveyComponent) customerSurveyComponent: CustomerSurveyComponent;
  @ViewChild('surveyForm') surveyForm: NgForm;

  protected _survey = signal<CustomerSurvey>(undefined);
  get survey() {
    return this._survey();
  }

  private readonly projectManagerJobFamily = 'PPM';
  private currentMercerDataDiffer: KeyValueDiffer<string, MercerData>;
  private validResultAnswerIds: string[];
  private surveys: CustomerSurvey[] = [MERCER_DEFAULT_SURVEY, MERCER_PROJECT_MANAGEMENT_SURVEY];
  private selectedSurveyType = MercerSurveyType.Default;

  private get selectedSurvey(): CustomerSurvey {
    const survey = this.surveys.find(survey => survey.surveyType === this.selectedSurveyType);
    return TinyHelpers.nonThrowableStructuredCloneWorkaround(survey ?? this.surveys[0]); // clone-problem: CustomerSurvey is a class, should not cause a problem yet though
  }

  public get isSurveyEditable(): boolean {
    return (
      this.isEditable ||
      (MercerSupportHelper.hasSpecialization(this.currentMercerData) && this.arePositionSpecsEditable)
    );
  }

  public get arePositionSpecsEditable(): boolean {
    return MercerSupportHelper.arePositionSpecsEditable(
      this.dialogType,
      this.memberDetails,
      this.store,
      this.permissionsService
    );
  }

  public get isValidationEditable(): boolean {
    return MercerSupportHelper.isValidationEditable(
      this.memberDetails,
      this.store,
      this.dialogType,
      this.permissionsService
    );
  }

  ngOnInit(): void {
    this.listenToTabChangeAndOpenSurveyIfNecessary();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.currentMercerData && !!this.currentMercerData) {
      if (!this.currentMercerDataDiffer) {
        this.currentMercerDataDiffer = this.differs.find(this.currentMercerData).create();
        this.updateSurveyBasedOnMercerData().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
      }
    }

    if (
      changes.memberDetails &&
      this.memberDetails &&
      !this.isMassDialog &&
      (changes.memberDetails.firstChange || this.dialogType !== MercerDialogType.Headcount)
    ) {
      this.loadSavedSurvey(!changes.memberDetails.previousValue);
    }
  }

  ngDoCheck(): void {
    const mercerDataChanges = this.currentMercerDataDiffer?.diff(
      this.currentMercerData as unknown as Map<string, MercerData>
    );
    const hasMercerDataChanges = this.currentMercerData && mercerDataChanges;

    if (!hasMercerDataChanges) {
      return;
    }

    mercerDataChanges.forEachChangedItem(item => {
      if (
        [
          MercerJobCodeSearchablePropertyEnum.FamilyCode,
          MercerJobCodeSearchablePropertyEnum.SubFamilyCode,
          MercerJobCodeSearchablePropertyEnum.SpecializationCode
        ].includes(item.key as MercerJobCodeSearchablePropertyEnum)
      ) {
        this.updateSurveyBasedOnMercerData(!this.isMassDialog).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
      }
    });
  }

  private resetSurvey(id: string = null): void {
    this._survey.set({ ...this.selectedSurvey, id });
  }

  private loadSavedSurvey(isFirstLoad: boolean): void {
    const loadSurvey$ = this.memberDetails.id
      ? this.customerSurveyService.getSurvey(this.memberDetails.id, !isFirstLoad).pipe(
          map(result => result ?? this.survey),
          tap(() => this.resetSurvey())
        )
      : of(this.survey);

    loadSurvey$
      .pipe(
        filter(survey => !!survey),
        delayWhen(this.loadSurvey),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private listenToTabChangeAndOpenSurveyIfNecessary(): void {
    this.mercerSupportDetailsService.currentTab$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(tabInfo => {
      if (
        tabInfo?.currentTab === MercerSupportDetailsComponentTab.Survey &&
        tabInfo?.previousTab === MercerSupportDetailsComponentTab.JobProfile
      ) {
        this.setSurveyTypeBySeasonSettings();
      }
    });
  }

  // "Start Survey" button
  private setSurveyTypeBySeasonSettings(): void {
    const askForProjectManagerResponsibilities =
      MercerSupportHelper.getSeasonSettings(this.dialogType, this.store)?.questionnaireDecision ===
        MercerSurveyDecision.JobFamilyBasis && this.currentMercerData?.familyCode === this.projectManagerJobFamily;

    if (!askForProjectManagerResponsibilities) {
      this.selectedSurveyType = MercerSurveyType.Default;
      this.updateSurveyBasedOnMercerData().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
      this.surveyForm?.form?.markAsTouched();
    } else {
      this.dialog
        .open(ConfirmationDialogComponent, {
          disableClose: true,
          data: {
            headline: 'mercer-support.dialog.project-manager-questionnaire-question-headline',
            msg: 'mercer-support.dialog.project-manager-questionnaire-question-message',
            cancelMsg: 'general.no',
            confirmMsg: 'general.yes',
            translate: true
          }
        })
        .afterClosed()
        .pipe(
          tap(hasConfirmed => {
            this.selectedSurveyType = hasConfirmed ? MercerSurveyType.ProjectManagement : MercerSurveyType.Default;
          }),
          switchMap(() => this.updateSurveyBasedOnMercerData()),
          tap(() => {
            this.surveyForm?.form?.markAsTouched();
            this.changeDetectorRef.detectChanges();
            this.customerSurveyComponent?.resetSurvey();
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();
    }
  }

  private mercerDataCache = new ObservableCachingHelper<[string[], string[]]>();
  private updateSurveyBasedOnMercerData(reset = true): Observable<[string[], string[]]> {
    if (reset) this.resetSurvey(this.survey?.id);

    if (
      this.selectedSurveyType === MercerSurveyType.Default &&
      MercerSupportHelper.hasSpecialization(this.currentMercerData)
    ) {
      const mercerDataWithoutCareerInformation = this.getMercerDataWithoutCareerInformation();

      return this.mercerDataCache
        .cacheSetOnMiss$(
          forkJoin([
            this.mercerDataService
              .getMercerFieldValues(
                0,
                1_000,
                MercerJobCodeSearchablePropertyEnum.CareerStream,
                mercerDataWithoutCareerInformation
              )
              .pipe(map(result => result?.content)),
            this.mercerDataService
              .getMercerFieldValues(
                0,
                1_000,
                MercerJobCodeSearchablePropertyEnum.CareerLevelShort,
                mercerDataWithoutCareerInformation
              )
              .pipe(map(result => result?.content))
          ]),
          Object.values(mercerDataWithoutCareerInformation).sort(),
          true
        )
        .pipe(
          tap(([validCareerStreamValues, validCareerLevelShortValues]) =>
            this.filterSurvey(validCareerStreamValues, validCareerLevelShortValues)
          )
        );
    }

    return of(null);
  }

  private getMercerDataWithoutCareerInformation(): MercerData {
    const mercerDataCopy = TinyHelpers.nonThrowableStructuredCloneWorkaround(this.currentMercerData); // clone-problem: MercerData is a class, should not cause a problem yet though

    Object.keys(mercerDataCopy).forEach(key => {
      if (key?.includes('career')) {
        delete mercerDataCopy[key];
      }
    });

    return mercerDataCopy;
  }

  private filterSurvey(validCareerStreamValues: string[], validCareerLevelShortValues: string[]): void {
    const validResults = this.survey.results.filter(
      result =>
        result.differentSurveyType ||
        (validCareerStreamValues.some(value => result.careerStream?.toLowerCase() === value?.toLowerCase()) &&
          validCareerLevelShortValues.some(value => result.careerLevelShort?.toLowerCase() === value?.toLowerCase()))
    );

    this.validResultAnswerIds = this.getMatchingAnswerIds(validResults);
    this.addParentAnswerIds(this.validResultAnswerIds);
    this.survey.questions = this.survey.questions.map(question => ({
      ...question,
      answers: question.answers.filter(answer => this.validResultAnswerIds.includes(answer.id))
    }));
  }

  private addParentAnswerIds(validAnswerIds: string[]): void {
    const validQuestions = this.survey.questions.filter(question =>
      question.answers.some(answer => validAnswerIds.includes(answer.id))
    );
    const validParentAnswerIds = this.getMatchingAnswerIds(validQuestions);

    if (!validParentAnswerIds?.length) {
      return;
    }

    this.validResultAnswerIds = [...this.validResultAnswerIds, ...validParentAnswerIds];

    this.addParentAnswerIds(validParentAnswerIds);
  }

  private getMatchingAnswerIds(items: CustomerSurveyResult[] | CustomerSurveyQuestion[]): string[] {
    return this.survey.questions.reduce(
      (answerIds, question) => [
        ...answerIds,
        ...question.answers
          .filter(answer =>
            items.some(item => item.id === (answer.linkedSurveyQuestionId ?? answer.linkedSurveyResultId))
          )
          .map(answer => answer.id)
      ],
      []
    );
  }

  public checkForResultAndProcessIt(): void {
    const result = MercerSupportHelper.getSurveyResult(this.survey);

    if (!result) {
      return;
    }

    if (result.differentSurveyType) {
      this.selectedSurveyType = result.differentSurveyType;
      this.toast.info(this.translate.instant('mercer-support.dialog.questionnaire-changed-info-message'));
      this.resetSurvey();
      return;
    }

    const consistencyCheck$ =
      this.survey.surveyType === MercerSurveyType.ProjectManagement ||
      this.dialogType === MercerDialogType.Headcount ||
      this.dialogType === MercerDialogType.StandingSupport
        ? of(null)
        : this.mercerService.performConsistencyCheck(this.memberDetails.seasonId, this.memberDetails.id, {
            careerLevel: result.careerLevelShort,
            careerStream: result.careerStream,
            positionClass: result.positionClass
          });

    consistencyCheck$
      .pipe(
        switchMap(() => this.saveOrCreateSurvey()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (this.dialogType === MercerDialogType.Headcount) {
          this.setCareerInformationHeadcount.emit(this.survey);
          return;
        }

        if (this.selectedSurveyType === MercerSurveyType.Default) {
          this.setCareerInformation.emit(result);
        } else if (this.selectedSurveyType === MercerSurveyType.ProjectManagement) {
          this.setJobCodeAndPositionClass.emit(result);
        }
      });
  }

  private saveOrCreateSurvey(): Observable<CustomerSurvey> {
    let action$: Observable<CustomerSurvey>;
    if (this.isMassDialog || this.dialogType === MercerDialogType.Headcount) {
      action$ = of(this.survey);
    } else {
      action$ = this.survey?.id ? this.updateSurveyResult() : this.createSurveyResult();
    }

    return action$.pipe(delayWhen(this.loadSurvey), take(1));
  }

  private createSurveyResult(): Observable<CustomerSurvey> {
    this.survey.id = this.memberDetails.id;
    return this.customerSurveyService.createSurvey(this.survey).pipe(delayWhen(this.loadSurvey));
  }

  private updateSurveyResult(): Observable<CustomerSurvey> {
    return this.customerSurveyService.updateSurvey(this.survey);
  }

  private loadSurvey = (survey: CustomerSurvey): Observable<[string[], string[]]> => {
    const inferSurveyType = (): MercerSurveyType => {
      const questionIds = survey.questions.map(question => question.id);
      const matchedQuestions = this.surveys.map(survey => ({
        surveyType: survey.surveyType,
        count: survey.questions.reduce((count, question) => (questionIds.includes(question.id) ? count + 1 : count), 0)
      }));
      return matchedQuestions.find(s => s.count === Math.max(...matchedQuestions.map(s => s.count))).surveyType;
    };

    // for old surveys, infer type based on matching questions
    this.selectedSurveyType = survey.surveyType ?? inferSurveyType();

    this._survey.set({
      ...survey,
      surveyType: this.selectedSurveyType,
      results: this.selectedSurvey.results,
      questions: this.selectedSurvey.questions.map(question => {
        const loadedQuestion = survey.questions?.find(({ id }) => id === question.id);
        if (loadedQuestion) {
          question.isActive = loadedQuestion.isActive;
          question.selectedAnswerId = loadedQuestion.selectedAnswerId;
          question.answers = question.answers.map(answer => ({
            ...answer,
            isSelected: answer.id === question.selectedAnswerId
          }));
        }
        return question;
      })
    });

    this.surveyForm?.form.markAsUntouched();
    return this.updateSurveyBasedOnMercerData(false);
  };
}
